/**
 * ShippingSizePicker — visible on the Checkout page ONLY when the vendor's
 * category is in the platform's shipping_size_categories list. Lets the shopper
 * pick one of the 7 admin-configured parcel tiers; the selected multiplier is
 * applied server-side to the delivery fee at order placement.
 */
import React from 'react';
import { Package, Loader2 } from 'lucide-react';

const ShippingSizePicker = ({ sizes, value, onChange, baseFee, formatMoney, loading }) => {
  if (loading) {
    return (
      <section
        className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-2 text-sm text-slate-500"
        data-testid="shipping-size-picker-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" /> Loading parcel sizes…
      </section>
    );
  }
  if (!Array.isArray(sizes) || sizes.length === 0) return null;

  return (
    <section
      className="bg-white border border-slate-200 rounded-2xl p-5"
      data-testid="shipping-size-picker"
    >
      <div className="flex items-center gap-2 mb-1">
        <Package className="h-5 w-5 text-emerald-600" />
        <h2 className="font-semibold text-slate-900">Parcel size</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Pick the tier that best matches your shipment. Larger parcels cost more to deliver.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {sizes.map((s) => {
          const active = value === s.id;
          const mult = Number(s.price_multiplier || 1);
          const estimated = baseFee != null ? formatMoney(baseFee * mult) : null;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onChange(s.id)}
              className={`text-left rounded-xl border p-3 transition
                ${active
                  ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/20'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'}`}
              data-testid={`shipping-size-option-${s.id}`}
              aria-pressed={active}
            >
              <div className={`text-sm font-semibold ${active ? 'text-emerald-800' : 'text-slate-900'}`}>
                {s.name}
              </div>
              <div className={`text-[11px] mt-0.5 ${active ? 'text-emerald-700' : 'text-slate-500'}`}>
                Up to {s.max_weight_kg}kg · {s.length_cm}×{s.width_cm}×{s.height_cm}cm
              </div>
              {estimated && (
                <div className="mt-2 text-xs">
                  <span className="text-slate-400 mr-1">Delivery ≈</span>
                  <span className={`font-semibold ${active ? 'text-emerald-800' : 'text-slate-800'}`}>
                    {estimated}
                  </span>
                </div>
              )}
              <div className="mt-1 text-[10px] text-slate-400 uppercase tracking-wide">
                ×{mult.toFixed(1)}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default ShippingSizePicker;
